import { Spinner } from '@heroui/react';
import { FirebaseError } from 'firebase/app';
import { Eye, EyeOff, Lock, Mail } from 'lucide-react';
import {
  FC, useEffect, useLayoutEffect, useState,
} from 'react';
import { useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';

import { useHeroToast, warmupFunction } from '@/hooks';
import { getBrowserLanguage, PANEL_ACCESS_DENIED_KEY, PanelAccessError, useLanguageState, useUserState } from '@/state';
import { LocaleLink, LocaleNavigate, useLocaleNavigate } from '@/utils/locale';

import { i18n } from '../../../utils/i18n';
import {
  AuthForm,
  CheckboxWrapper,
  FieldError,
  ForgotPasswordLink,
  InputGroup,
  InputIcon,
  InputLabel,
  InputWrapper,
  LegalDisclaimer,
  PasswordToggle,
  RememberRow,
  StyledInput,
  SubmitButton,
  WelcomeSubtitle,
  WelcomeTitle,
} from '../auth.styled';
import { AuthLayout } from '../components/AuthLayout';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface LoginFormProps {
  email: string;
  password: string;
}

export const SignIn: FC = () => {
  const { t } = useTranslation();
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormProps>({ mode: 'onTouched' });
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [isGoogleLoggingIn, setIsGoogleLoggingIn] = useState(false);
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const { showError } = useHeroToast();
  const { signInWithEmail, signInWithGoogle, currentUser, isResolved } = useUserState();
  const { language, setLanguage } = useLanguageState();
  const navigate = useLocaleNavigate();

  // Pobierz chunk Dashboardu, zanim użytkownik skończy wpisywać hasło. Po `navigate('/')`
  // trasa jest leniwa (`lazy()` w App.tsx) i bez tego Suspense pokazywał kolejny loader
  // w treści, choć sidebar był już na ekranie. Formularz logowania i tak stoi bezczynnie.
  useEffect(() => {
    void import('@/views/Dashboard/Dashboard');
  }, []);

  // Monit „brak dostępu do panelu" po ścieżce, która wylogowała gościa poza
  // formularzem (np. reload z przywróconą sesją gościa — patrz userState).
  useLayoutEffect(() => {
    try {
      if (sessionStorage.getItem(PANEL_ACCESS_DENIED_KEY)) {
        sessionStorage.removeItem(PANEL_ACCESS_DENIED_KEY);
        showError(t(
          'auth.errors.panelNoAccess',
          'To konto nie ma dostępu do panelu. Zaloguj się kontem personelu hotelu.',
        ));
      }
    } catch { /* ignore */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Initialize language from browser on first mount
  useLayoutEffect(() => {
    const stored = localStorage.getItem('language-storage');
    if (!stored) {
      const browserLang = getBrowserLanguage();
      setLanguage(browserLang);
      i18n.changeLanguage(browserLang);
    } else if (language && language !== i18n.language) {
      i18n.changeLanguage(language);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Redirect if already logged in (only after auth state is resolved)
  if (isResolved && currentUser) {
    return <LocaleNavigate to="/" replace />;
  }

  const getFirebaseErrorMessage = (error: unknown): string => {
    // Konto bez dostępu do panelu (np. gość aplikacji gościa) — Firebase Auth
    // je zalogował, ale personelem nie jest. Pokazujemy jasny monit.
    if (error instanceof PanelAccessError || (error as { code?: string })?.code === 'panel/no-access') {
      return t(
        'auth.errors.panelNoAccess',
        'To konto nie ma dostępu do panelu. Zaloguj się kontem personelu hotelu.',
      );
    }
    if (error instanceof FirebaseError) {
      const errorKey = `auth.errors.${error.code}`;
      const translated = t(errorKey);
      if (translated !== errorKey) {
        return translated;
      }
    }
    return t('auth.errors.default');
  };

  const handleGoogleSignIn = async (): Promise<void> => {
    setIsGoogleLoggingIn(true);
    // Pre-grzanie funkcji wołanej zaraz po wejściu do panelu — grzeje się w trakcie
    // round-tripu logowania, więc strzał przy ładowaniu panelu trafia w ciepłą instancję.
    warmupFunction('v3-getOrganizationsByIds');
    try {
      const user = await signInWithGoogle(rememberMe);

      // Check if email is verified (for consistency with email login)
      await user.reload();

      // Check if this is first login
      const createdAt = new Date(user.metadata.creationTime || 0).getTime();
      const lastSignIn = new Date(user.metadata.lastSignInTime || 0).getTime();
      const isFirstLogin = Math.abs(lastSignIn - createdAt) < 5000; // Within 5 seconds

      // For backwards compatibility: only check verification for new users
      const implementationDate = new Date('2026-02-22').getTime();
      const shouldCheckVerification = createdAt >= implementationDate;

      if (shouldCheckVerification && !user.emailVerified) {
        // Redirect to verify-email if email is not verified
        navigate('/verify-email', { state: { email: user.email } });
        return;
      }

      // Don't setIsGoogleLoggingIn(false) - keep showing loader during redirect
      navigate('/');
    } catch (error) {
      showError(getFirebaseErrorMessage(error));
      setIsGoogleLoggingIn(false);
    }
  };

  const onSubmit = async (formData: LoginFormProps): Promise<void> => {
    setIsLoggingIn(true);
    // Pre-grzanie funkcji wołanej zaraz po wejściu do panelu (patrz handleGoogleSignIn).
    warmupFunction('v3-getOrganizationsByIds');
    try {
      const user = await signInWithEmail(formData.email, formData.password, rememberMe);

      // Check if email is verified
      await user.reload();

      // Check if this is first login (createdAt ~= lastSignInTime)
      const createdAt = new Date(user.metadata.creationTime || 0).getTime();
      const lastSignIn = new Date(user.metadata.lastSignInTime || 0).getTime();
      const isFirstLogin = Math.abs(lastSignIn - createdAt) < 5000; // Within 5 seconds

      // For backwards compatibility: only check verification for new users (created after implementation)
      // Users created before 2026-02-22 can log in without verification
      const implementationDate = new Date('2026-02-22').getTime();
      const shouldCheckVerification = createdAt >= implementationDate;

      if (shouldCheckVerification && !user.emailVerified) {
        // Redirect to verify-email if email is not verified
        // Keep user logged in so polling can work
        // Don't setIsLoggingIn(false) - keep showing loader during redirect
        navigate('/verify-email', { state: { email: formData.email } });
        return;
      }

      // Don't setIsLoggingIn(false) - keep showing loader during redirect
      navigate('/');
    } catch (error) {
      showError(getFirebaseErrorMessage(error));
      setIsLoggingIn(false);
    }
  };

  return (
    <AuthLayout>
      <WelcomeTitle>{t('auth.signIn.welcomeTitle')}</WelcomeTitle>
      <WelcomeSubtitle>{t('auth.signIn.welcomeSubtitle')}</WelcomeSubtitle>

      <AuthForm onSubmit={handleSubmit(onSubmit)} noValidate>
        <InputGroup>
          <InputLabel htmlFor="sign-in-email">{t('auth.signIn.emailLabel')}</InputLabel>
          <InputWrapper>
            <InputIcon>
              <Mail aria-hidden="true" />
            </InputIcon>
            <StyledInput
              id="sign-in-email"
              type="email"
              autoComplete="email"
              inputMode="email"
              placeholder={t('auth.signIn.emailPlaceholder')}
              aria-invalid={errors.email ? 'true' : 'false'}
              aria-describedby={errors.email ? 'sign-in-email-error' : undefined}
              {...register('email', {
                required: t('auth.signIn.emailRequired'),
                pattern: {
                  value: EMAIL_PATTERN,
                  message: t('auth.signIn.emailInvalid'),
                },
              })}
            />
          </InputWrapper>
          {errors.email && <FieldError id="sign-in-email-error" role="alert">{errors.email.message}</FieldError>}
        </InputGroup>

        <InputGroup>
          <InputLabel htmlFor="sign-in-password">{t('auth.signIn.passwordLabel')}</InputLabel>
          <InputWrapper>
            <InputIcon>
              <Lock aria-hidden="true" />
            </InputIcon>
            <StyledInput
              id="sign-in-password"
              type={isPasswordVisible ? 'text' : 'password'}
              autoComplete="current-password"
              placeholder={t('auth.signIn.passwordPlaceholder')}
              aria-invalid={errors.password ? 'true' : 'false'}
              aria-describedby={errors.password ? 'sign-in-password-error' : undefined}
              {...register('password', { required: t('auth.signIn.passwordRequired') })}
            />
            <PasswordToggle
              type="button"
              onClick={() => setIsPasswordVisible(!isPasswordVisible)}
              aria-label={isPasswordVisible ? t('auth.signIn.hidePassword') : t('auth.signIn.showPassword')}
              aria-pressed={isPasswordVisible}
            >
              {isPasswordVisible ? <EyeOff aria-hidden="true" /> : <Eye aria-hidden="true" />}
            </PasswordToggle>
          </InputWrapper>
          {errors.password && (
            <FieldError id="sign-in-password-error" role="alert">{errors.password.message}</FieldError>
          )}
        </InputGroup>

        <RememberRow>
          <CheckboxWrapper>
            <input
              type="checkbox"
              checked={rememberMe}
              onChange={(e) => setRememberMe(e.target.checked)}
            />
            {t('auth.signIn.rememberMe')}
          </CheckboxWrapper>
          <LocaleLink to="/forgot-password">
            <ForgotPasswordLink as="span">
              {t('auth.signIn.forgotPassword')}
            </ForgotPasswordLink>
          </LocaleLink>
        </RememberRow>

        <SubmitButton type="submit" disabled={isLoggingIn} aria-busy={isLoggingIn}>
          {isLoggingIn && (
            <Spinner
              size="sm"
              classNames={{
                circle1: 'border-b-white',
                circle2: 'border-b-white',
              }}
            />
          )}
          <span>{isLoggingIn ? t('auth.signIn.signingIn') : t('auth.signIn.signInButton')}</span>
        </SubmitButton>
      </AuthForm>

      <LegalDisclaimer>
        <Trans
          i18nKey="auth.signIn.legalDisclaimer"
          components={{
            terms: <a href="/pl/regulamin" target="_blank" rel="noopener noreferrer" />,
            privacy: <a href="/privacy" target="_blank" rel="noopener noreferrer" />,
            cookies: <a href="/pl/cookies" target="_blank" rel="noopener noreferrer" />,
          }}
        />
      </LegalDisclaimer>
    </AuthLayout>
  );
};
